import {
  Building2,
  CheckSquare,
  ChevronDown,
  ClipboardList,
  Filter,
  Flag,
  Inbox,
  LayoutDashboard,
  Link2,
  Megaphone,
  Menu,
  MessageCircle,
  Monitor,
  QrCode,
  Search,
  Settings,
  Smartphone,
  Sparkles,
  Tablet,
  Trash2,
  UserRound,
  UserRoundPlus,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { cn } from '../../lib/cn'
import { useUIStore, type ViewMode } from '../../stores/uiStore'
import { useClientStore } from '../../stores/clientStore'
import {
  useAgenticConversations,
  useDeleteAgenticConversation,
} from '../../features/agentic-ai/hooks'

interface NavRowProps {
  to?: string
  label: string
  icon: LucideIcon
  collapsed?: boolean
  onClick?: () => void
  disabled?: boolean
}

function NavRow({ to, label, icon: Icon, collapsed, onClick, disabled }: NavRowProps) {
  const baseClasses = cn(
    'group relative flex items-center gap-3 rounded-pill text-body-sm font-450 transition-colors',
    'focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black',
    collapsed ? 'mx-auto w-10 h-10 justify-center' : 'px-4 py-2',
  )

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        title={collapsed ? `${label} (Unavailable)` : undefined}
        className={cn(baseClasses, 'w-full cursor-not-allowed text-left text-black/30')}
      >
        <Icon size={18} className="shrink-0 text-black/25" />
        {!collapsed && <span className="truncate">{label}</span>}
      </button>
    )
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={collapsed ? label : undefined}
        className={cn(
          baseClasses,
          'text-black/75 hover:bg-black/[0.04] hover:text-black w-full text-left',
        )}
      >
        <Icon size={18} className="shrink-0 text-black/60" />
        {!collapsed && <span className="truncate">{label}</span>}
      </button>
    )
  }

  return (
    <NavLink
      to={to!}
      end
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          baseClasses,
          isActive
            ? 'bg-gradient-to-r from-brand-blue/[0.18] via-brand-violet/[0.16] to-brand-pink/[0.20] text-black font-540'
            : 'text-black/75 hover:bg-black/[0.04] hover:text-black',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            size={18}
            className={cn('shrink-0', isActive ? 'text-brand-violet' : 'text-black/60')}
          />
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  )
}

interface NavChild {
  to: string
  label: string
  /** Match the route exactly (NavLink `end`). Default true. */
  end?: boolean
}

interface NavGroupProps {
  label: string
  icon: LucideIcon
  collapsed?: boolean
  /** Route the parent icon links to when the sidebar is collapsed. */
  baseTo: string
  items: NavChild[]
}

function NavGroup({ label, icon: Icon, collapsed, baseTo, items }: NavGroupProps) {
  const location = useLocation()
  const childActive = items.some((c) =>
    c.end === false ? location.pathname.startsWith(c.to) : location.pathname === c.to,
  )
  const [open, setOpen] = useState(childActive)

  // When collapsed, fall back to a plain icon link to the group's base route.
  if (collapsed) {
    return <NavRow to={baseTo} icon={Icon} label={label} collapsed />
  }

  return (
    <div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation() // keep the mobile drawer open while toggling
          setOpen((o) => !o)
        }}
        aria-expanded={open}
        className={cn(
          'group relative flex w-full items-center gap-3 rounded-pill px-4 py-2 text-left text-body-sm font-450 transition-colors',
          'focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black',
          childActive && !open
            ? 'bg-gradient-to-r from-brand-blue/[0.18] via-brand-violet/[0.16] to-brand-pink/[0.20] text-black font-540'
            : 'text-black/75 hover:bg-black/[0.04] hover:text-black',
        )}
      >
        <Icon
          size={18}
          className={cn('shrink-0', childActive ? 'text-brand-violet' : 'text-black/60')}
        />
        <span className="flex-1 truncate">{label}</span>
        <ChevronDown
          size={14}
          className={cn('shrink-0 text-black/40 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="mt-0.5 flex flex-col gap-0.5">
          {items.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end={child.end !== false}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-3 rounded-pill py-1.5 pl-11 pr-4 text-body-sm font-450 transition-colors',
                  'focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black',
                  isActive
                    ? 'bg-gradient-to-r from-brand-blue/[0.18] via-brand-violet/[0.16] to-brand-pink/[0.20] text-black font-540'
                    : 'text-black/65 hover:bg-black/[0.04] hover:text-black',
                )
              }
            >
              <span className="truncate">{child.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

function SectionLabel({
  label,
  collapsed,
  open,
  onToggle,
}: {
  label: string
  collapsed: boolean
  open: boolean
  onToggle: () => void
}) {
  if (collapsed) {
    return <div className="mt-3 mb-1 mx-3 border-t border-black/[0.06]" />
  }
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={(event) => {
        event.stopPropagation()
        onToggle()
      }}
      className="group mx-1 mt-3 mb-1 flex w-[calc(100%-0.5rem)] items-center gap-2 rounded-lg px-3 py-1.5 text-left transition-colors hover:bg-black/[0.04] focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
    >
      <span className="text-[9px] uppercase tracking-[0.12em] font-540 text-black/35 whitespace-nowrap shrink-0">
        {label}
      </span>
      <div className="flex-1 border-t border-black/[0.06]" />
      <ChevronDown
        size={12}
        className={cn(
          'shrink-0 text-black/30 transition-transform group-hover:text-black/50',
          open && 'rotate-180',
        )}
      />
    </button>
  )
}

function AgenticChatHistory({ clientId }: { clientId: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const conversations = useAgenticConversations(clientId)
  const removeConversation = useDeleteAgenticConversation(clientId)
  const selectedId = new URLSearchParams(location.search).get('chat')

  return (
    <div className="mx-1 mb-2 min-h-0">
      <p className="px-3 pb-1 pt-3 text-[10px] font-540 uppercase tracking-[0.12em] text-black/35">
        Chats
      </p>
      <div className="flex max-h-[38dvh] flex-col gap-0.5 overflow-y-auto pr-1">
        {conversations.isLoading && (
          <p className="px-3 py-2 text-body-sm text-black/35">กำลังโหลด…</p>
        )}
        {conversations.data?.map((conversation) => (
          <div
            key={conversation.id}
            className={cn(
              'group/chat flex items-center rounded-xl transition-colors',
              selectedId === conversation.id
                ? 'bg-black/[0.07] text-black'
                : 'text-black/65 hover:bg-black/[0.04] hover:text-black',
            )}
          >
            <button
              type="button"
              title={conversation.title}
              onClick={() => {
                navigate(`/clients/${clientId}/agentic-ai?chat=${conversation.id}`)
              }}
              className="min-w-0 flex-1 truncate px-3 py-2 text-left text-body-sm font-400"
            >
              {conversation.title}
            </button>
            <button
              type="button"
              aria-label={`ลบ ${conversation.title}`}
              title="ลบบทสนทนา"
              disabled={removeConversation.isPending}
              onClick={(event) => {
                event.stopPropagation()
                if (!window.confirm(`ลบบทสนทนา “${conversation.title}” หรือไม่?`)) return
                removeConversation.mutate(conversation.id, {
                  onSuccess: () => {
                    if (selectedId === conversation.id) navigate(`/clients/${clientId}/agentic-ai`)
                  },
                })
              }}
              className="mr-1 inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-black/30 opacity-0 transition hover:bg-black/[0.06] hover:text-red-600 group-hover/chat:opacity-100 focus:opacity-100"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        {!conversations.isLoading && conversations.data?.length === 0 && (
          <p className="px-3 py-2 text-body-sm text-black/35">ยังไม่มีบทสนทนา</p>
        )}
      </div>
    </div>
  )
}

export function Sidebar() {
  const { clientId: routeClientId } = useParams<{ clientId: string }>()
  const storeClientId = useClientStore((s) => s.currentClientId)
  const clientId = routeClientId ?? storeClientId ?? undefined
  const navigate = useNavigate()
  const location = useLocation()
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen)
  const setMobileOpen = useUIStore((s) => s.setMobileSidebarOpen)
  const viewMode = useUIStore((s) => s.viewMode)
  const setViewMode = useUIStore((s) => s.setViewMode)
  const storePlanCreateOpen = useUIStore((s) => s.sidebarSubmenus['plan-create'] ?? true)
  const storeSourceDataOpen = useUIStore((s) => s.sidebarSubmenus['source-data'] ?? true)
  const storeAnalyticsOpen = useUIStore((s) => s.sidebarSubmenus['analytics-tracking'] ?? true)
  const storeWorkspaceOpen = useUIStore((s) => s.sidebarSubmenus.workspace ?? true)
  const toggleStoreSubmenu = useUIStore((s) => s.toggleSidebarSubmenu)
  const isAgenticPage = !!clientId && location.pathname === `/clients/${clientId}/agentic-ai`
  const compact = collapsed && !mobileOpen

  // On the Agentic page, auto-collapse the section menus so the sidebar stays
  // focused on the chat history. Kept in local state so it never clobbers the
  // user's persisted preferences — sections restore when they leave the page,
  // and can still be toggled open manually while here.
  const [agenticOpen, setAgenticOpen] = useState<Record<string, boolean>>({})
  useEffect(() => {
    if (isAgenticPage) setAgenticOpen({})
  }, [isAgenticPage])

  const submenuOpen = (key: string, stored: boolean) =>
    isAgenticPage ? (agenticOpen[key] ?? false) : stored
  const toggleSidebarSubmenu = (key: string) => {
    if (isAgenticPage) setAgenticOpen((o) => ({ ...o, [key]: !(o[key] ?? false) }))
    else toggleStoreSubmenu(key)
  }

  const planCreateOpen = submenuOpen('plan-create', storePlanCreateOpen)
  const sourceDataOpen = submenuOpen('source-data', storeSourceDataOpen)
  const analyticsOpen = submenuOpen('analytics-tracking', storeAnalyticsOpen)
  const workspaceOpen = submenuOpen('workspace', storeWorkspaceOpen)

  const handleNavClick = () => setMobileOpen(false)

  function handleSearchClick() {
    if (clientId) {
      setMobileOpen(false)
      navigate(`/clients/${clientId}/search`)
    }
  }

  return (
    <aside
      className={cn(
        'shrink-0 border-r border-black/[0.05] bg-white flex flex-col overflow-y-auto z-40',
        'transition-[width,transform] duration-200',
        // Narrow shell (< @md = 448px): fixed slide-over, off-screen by default.
        // Wider shell: static in flow.
        'fixed top-0 bottom-0 left-0 @md/shell:static',
        mobileOpen ? 'translate-x-0' : '-translate-x-full @md/shell:translate-x-0',
        mobileOpen ? 'w-72' : collapsed ? 'w-14' : 'w-72',
      )}
    >
      {/* Header — hamburger + search icon */}
      <div
        className={cn(
          'pt-3',
          collapsed && !mobileOpen
            ? 'flex flex-col items-center gap-1 px-2'
            : 'flex items-center justify-between px-3',
        )}
      >
        <button
          type="button"
          onClick={mobileOpen ? () => setMobileOpen(false) : toggleSidebar}
          aria-label={mobileOpen ? 'Close menu' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={mobileOpen ? 'Close' : collapsed ? 'Expand' : 'Collapse'}
          className="inline-flex items-center justify-center size-10 rounded-lg text-black/70 hover:bg-black/[0.05] hover:text-black transition-colors"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
        {clientId && (
          <button
            type="button"
            aria-label="Search"
            title="Search"
            onClick={handleSearchClick}
            className="inline-flex items-center justify-center size-10 rounded-lg text-black/70 hover:bg-black/[0.05] hover:text-black transition-colors"
          >
            <Search size={17} />
          </button>
        )}
      </div>

      <nav
        className={cn('flex flex-col gap-1 pt-3', collapsed && !mobileOpen ? 'px-2' : 'px-3')}
        onClick={handleNavClick}
      >
        {clientId ? (
          <>
            {/* Daily workflow */}
            <NavRow
              to={`/clients/${clientId}/agentic-ai`}
              icon={Sparkles}
              label="Agentic AI"
              collapsed={collapsed && !mobileOpen}
            />
            <NavRow
              to="/review-center"
              icon={Inbox}
              label="AI Inbox"
              collapsed={collapsed && !mobileOpen}
            />
            <NavRow
              to={`/clients/${clientId}`}
              icon={LayoutDashboard}
              label="Overview"
              collapsed={collapsed && !mobileOpen}
            />

            {/* Plan & create */}
            <SectionLabel
              label="Plan & Create"
              collapsed={compact}
              open={planCreateOpen}
              onToggle={() => toggleSidebarSubmenu('plan-create')}
            />
            {(compact || planCreateOpen) && (
              <>
                <NavRow
                  to={`/clients/${clientId}/strategist-flow`}
                  icon={CheckSquare}
                  label="Strategy Flow"
                  collapsed={compact}
                />
                <NavRow
                  to={`/clients/${clientId}/campaigns`}
                  icon={Flag}
                  label="Campaigns"
                  collapsed={compact}
                />
                <NavRow
                  to={`/clients/${clientId}/content-center`}
                  icon={ClipboardList}
                  label="Content Center"
                  collapsed={compact}
                />
              </>
            )}

            {/* Source data */}
            <SectionLabel
              label="Source Data"
              collapsed={compact}
              open={sourceDataOpen}
              onToggle={() => toggleSidebarSubmenu('source-data')}
            />
            {(compact || sourceDataOpen) && (
              <>
                <NavRow
                  to={`/clients/${clientId}/customers`}
                  icon={UserRound}
                  label="Patient Center"
                  collapsed={compact}
                />
                <NavRow
                  to={`/clients/${clientId}/customer-journey/visit-records`}
                  icon={UserRoundPlus}
                  label="Visit Center"
                  collapsed={compact}
                />
                <NavRow
                  to={`/clients/${clientId}/lead-center`}
                  icon={Filter}
                  label="Lead Center"
                  collapsed={compact}
                />
                <NavRow
                  to={`/clients/${clientId}/chat-center`}
                  icon={MessageCircle}
                  label="Chat Center"
                  collapsed={compact}
                />
              </>
            )}

            {/* Analytics & tracking (DEV features) */}
            <SectionLabel
              label="Analytics & Tracking"
              collapsed={compact}
              open={analyticsOpen}
              onToggle={() => toggleSidebarSubmenu('analytics-tracking')}
            />
            {(compact || analyticsOpen) && (
              <>
                <NavRow
                  to={`/clients/${clientId}/sources`}
                  icon={Link2}
                  label="Sources"
                  collapsed={compact}
                />
                <NavGroup
                  label="Ads"
                  icon={Megaphone}
                  collapsed={compact}
                  baseTo={`/clients/${clientId}/ads/dashboard`}
                  items={[
                    { to: `/clients/${clientId}/ads/dashboard`, label: 'Overview' },
                    { to: `/clients/${clientId}/ads/campaigns`, label: 'Campaigns' },
                    { to: `/clients/${clientId}/ads/performance`, label: 'Performance' },
                  ]}
                />
                <NavGroup
                  label="QR Code Tracking"
                  icon={QrCode}
                  collapsed={compact}
                  baseTo={`/clients/${clientId}/qr-tracking`}
                  items={[
                    { to: `/clients/${clientId}/qr-tracking`, label: 'Overview' },
                    {
                      to: `/clients/${clientId}/qr-tracking/line-tracking-list`,
                      label: 'Tracking Links',
                    },
                    { to: `/clients/${clientId}/qr-tracking/line-users`, label: 'LINE Users' },
                  ]}
                />
              </>
            )}

            <SectionLabel
              label="Workspace"
              collapsed={compact}
              open={workspaceOpen}
              onToggle={() => toggleSidebarSubmenu('workspace')}
            />
            {(compact || workspaceOpen) && (
              <>
                <NavRow
                  to={`/clients/${clientId}/tasks`}
                  icon={CheckSquare}
                  label="Tasks"
                  collapsed={compact}
                />
                <NavRow to="/clients" icon={Building2} label="Clients" collapsed={compact} />
                <NavRow
                  to={`/clients/${clientId}/settings`}
                  icon={Settings}
                  label="Settings"
                  collapsed={compact}
                />
              </>
            )}
            {isAgenticPage && (!collapsed || mobileOpen) && <AgenticChatHistory clientId={clientId} />}
          </>
        ) : (
          <>
            <NavRow
              to="/dashboard"
              icon={LayoutDashboard}
              label="Dashboard"
              collapsed={collapsed && !mobileOpen}
            />
            <NavRow
              to="/clients"
              icon={Building2}
              label="Clients"
              collapsed={collapsed && !mobileOpen}
            />
          </>
        )}
      </nav>

      <div
        className={cn(
          'mt-auto border-t border-black/[0.05] flex items-center justify-between gap-2',
          collapsed && !mobileOpen ? 'px-2 py-3 flex-col' : 'px-4 py-3',
        )}
      >
        <span className="mono-tag text-black/40">v0.1.0</span>
        {(!collapsed || mobileOpen) && import.meta.env.DEV && (
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        )}
      </div>
    </aside>
  )
}

// ─── Dev tool: View-mode toggle ───────────────────────────────────────
const VIEW_OPTIONS: { key: ViewMode; icon: LucideIcon; label: string }[] = [
  { key: 'desktop', icon: Monitor, label: 'Desktop' },
  { key: 'tablet', icon: Tablet, label: 'Tablet' },
  { key: 'mobile', icon: Smartphone, label: 'Mobile' },
]

function ViewModeToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  return (
    <div
      className="flex items-center gap-0.5 p-0.5 rounded-pill bg-black/[0.04] border border-black/[0.06]"
      title="Dev: switch viewport preview (production จะไม่มีปุ่มนี้)"
    >
      {VIEW_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          aria-label={opt.label}
          title={opt.label}
          className={cn(
            'size-6 inline-flex items-center justify-center rounded-full transition-colors',
            value === opt.key
              ? 'bg-white text-brand-violet shadow-soft'
              : 'text-black/45 hover:text-black',
          )}
        >
          <opt.icon size={11} />
        </button>
      ))}
    </div>
  )
}
